/**
 * Admin categories API layer (P1 Tier-A). Typed calls to the SHIPPED
 * STORY-003-001 admin marketplace router (/admin/marketplace/categories*),
 * gated server-side on `marketplace.category.manage` (list AND mutate share
 * the slug). No backend routes added. Mirrors the Pydantic schemas
 * Category{Create,Update,Response,ListResponse}.
 *
 * PIM Wave P1 Task 5: extended for the tree upgrade. `AdminCategory` now
 * mirrors the FULL `CategoryResponse` (`marketplace/schemas.py:642-656`) —
 * `name_en`/`is_archived`/`photo_rules` were previously missing (this file
 * was stale, per recon C4/A16). `listAllAdminCategories()` drains the flat
 * cursor list (there is no admin tree endpoint — the tree is assembled
 * client-side from `parent_id`/`sort_order`/`depth`). `patchCategory()` is
 * the full-shape partial-update call (mirrors `CategoryUpdate` exactly,
 * including `photo_rules` — Task 6 will grow into the rest of it); it backs
 * the sibling sort_order reorder in `hooks.ts`. `updateCategory`/
 * `AdminCategoryUpdate` are UNCHANGED — `CategoryFormDialog` keeps using
 * them as-is.
 *
 * PIM Wave P1 Task 6: `AdminCategoryCreate` gains `name_en` (`CategoryCreate`
 * has always had it, `marketplace/schemas.py:608-618` — this file just
 * hadn't caught up). `CategoryFormDialog` now writes edits through
 * `patchCategory`/`AdminCategoryPatch` (changed-keys-only, covers
 * `name_en`/`photo_rules`) instead of `updateCategory`/`AdminCategoryUpdate`
 * — the latter two stay as-is (unused by the dialog now, left intact per
 * the Task 5 precedent of not deleting a still-typechecking, harmless
 * export). New in this task: category aliases (nested CRUD, no PATCH —
 * the chip editor is add/remove only, brief §1) and archive/unarchive
 * (dedicated POST routes, `admin_router.py:775-830`).
 *
 * PIM Wave P1 Task 7: `mergeCategory` — `POST .../merge`
 * (`admin_router.py:833-867`, `CategoryMergeRequest`/`CategoryMergeResponse`
 * at `marketplace/schemas.py:765-789`). USER RULING C2 (binding): this is
 * NOT a dry-run — there is no impact/preview endpoint, the call commits the
 * merge and returns carry-over counts. Verified guards (`service.py`
 * `merge_category`, lines 1289-1341): self-merge (422
 * `marketplace.category.merge_self`), target inside source's own subtree
 * (422 `marketplace.category.merge_target_descendant`), target archived
 * (422 `marketplace.category.merge_target_archived`), source+target sharing
 * a same-slug direct child (409 `marketplace.category.merge_child_slug_conflict`,
 * only detectable server-side). The SOURCE may already be archived — the
 * service docstring says so explicitly (line 1302: "The SOURCE may already
 * be archived") — merging an archived category away is a legal cleanup path,
 * so the UI does not gate the "Merge into…" row action on `is_archived`.
 */
import { apiClient, apiFetch } from "@/lib/api-client";

/** Mirrors `PhotoRules` (`marketplace/schemas.py:579-599`). */
export interface PhotoRules {
  min_photos: number | null;
  max_photos: number | null;
  min_edge_px: number | null;
}

export interface AdminCategory {
  id: string;
  slug: string;
  name_ru: string;
  name_ky: string | null;
  name_en: string | null;
  parent_id: string | null;
  sort_order: number;
  depth: number;
  is_archived: boolean;
  photo_rules: PhotoRules | null;
}
export interface AdminCategoryListResponse {
  categories: AdminCategory[];
  next_cursor: string | null;
}
export interface AdminCategoryCreate {
  slug: string;
  name_ru: string;
  name_ky: string | null;
  /** Optional (unlike `name_ky`, kept required for backward-compat with the
   * pre-Task-6 shape) — `CategoryCreate.name_en` is itself optional server
   * side (`marketplace/schemas.py:616`); `CategoryFormDialog` always sends
   * it explicitly, but this stays optional so any older caller that omits
   * it (it never existed before this task) still type-checks. */
  name_en?: string | null;
  parent_id: string | null;
  sort_order: number;
}
export type AdminCategoryUpdate = Partial<AdminCategoryCreate>;

/** Mirrors `CategoryAliasResponse` (`marketplace/schemas.py:748-757`). */
export interface CategoryAlias {
  id: string;
  category_id: string;
  alias: string;
  lang: string | null;
}
export interface CategoryAliasListResponse {
  aliases: CategoryAlias[];
}
/** Body for `POST .../aliases` — mirrors `CategoryAliasCreate`
 * (`marketplace/schemas.py:690-704`). No update DTO here: the chip editor
 * is add/remove only (Task 6 brief §1), so `CategoryAliasUpdate`/PATCH is
 * deliberately not wired. */
export interface CategoryAliasCreateBody {
  alias: string;
  lang?: "ru" | "ky" | "en" | null;
}

/**
 * Body for `PATCH /admin/marketplace/categories/{id}` — the FULL partial
 * shape, mirroring `CategoryUpdate` (`marketplace/schemas.py:621-639`)
 * exactly (unlike `AdminCategoryUpdate` above, which only covers the
 * `CategoryFormDialog` fields). Used by `patchCategory` — the sibling
 * reorder (`sort_order`-only patches) and, from Task 6 on, `photo_rules`/
 * `name_en` writes go through this.
 */
export interface AdminCategoryPatch {
  slug?: string;
  name_ru?: string;
  name_ky?: string | null;
  name_en?: string | null;
  parent_id?: string | null;
  sort_order?: number;
  photo_rules?: PhotoRules | null;
}

export const categoryKeys = {
  list: (filters: Record<string, unknown>) =>
    ["admin", "categories", filters] as const,
  /** The full-drain list query (`useAllAdminCategories`) — same
   * `["admin","categories",...]` prefix, so the existing
   * `useInvalidateCategories()` (prefix-match invalidate) covers it too. */
  all: () => ["admin", "categories", "all"] as const,
  /** `["admin","categories",categoryId,"aliases"]` — nested under the
   * categories family (same prefix-match reasoning as `attributeFieldKeys.
   * options`), keyed by category id. Task 6 brief §1: alias mutations
   * invalidate ONLY this key, never the categories list/detail family. */
  aliases: (categoryId: string) =>
    ["admin", "categories", categoryId, "aliases"] as const,
} as const;

export interface ListCategoriesParams {
  cursor?: string | null;
  limit?: number;
  parent_id?: string | null;
}
export function listCategories(
  params: ListCategoriesParams,
): Promise<AdminCategoryListResponse> {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.parent_id) query.set("parent_id", params.parent_id);
  const qs = query.toString();
  return apiClient.get<AdminCategoryListResponse>(
    `/admin/marketplace/categories${qs ? `?${qs}` : ""}`,
  );
}

/** Mirrors the backend's `MAX_LIST_LIMIT` (`marketplace/repository.py:132`)
 * — draining should page at the server's max, not its default. */
const DRAIN_LIMIT = 100;

/**
 * Drains `GET /admin/marketplace/categories` (flat, cursor-paginated) at
 * the max page size until `next_cursor` is null, returning the complete
 * flat list. There is no admin tree endpoint (recon C4) — every consumer
 * that needs the whole set (the tree view) goes through this.
 */
export async function listAllAdminCategories(): Promise<AdminCategory[]> {
  const all: AdminCategory[] = [];
  let cursor: string | null = null;
  for (;;) {
    const page: AdminCategoryListResponse = await listCategories({
      cursor,
      limit: DRAIN_LIMIT,
    });
    all.push(...page.categories);
    if (!page.next_cursor) break;
    cursor = page.next_cursor;
  }
  return all;
}
export function createCategory(body: AdminCategoryCreate): Promise<AdminCategory> {
  return apiClient.post<AdminCategory, AdminCategoryCreate>(
    "/admin/marketplace/categories",
    body,
  );
}
export function updateCategory(
  id: string,
  body: AdminCategoryUpdate,
): Promise<AdminCategory> {
  // PATCH partial-update (matches the seller precedent; no apiClient.patch shortcut).
  return apiFetch<AdminCategory, AdminCategoryUpdate>({
    method: "PATCH",
    path: `/admin/marketplace/categories/${id}`,
    body,
  });
}
export function deleteCategory(id: string): Promise<AdminCategory> {
  return apiClient.delete<AdminCategory>(`/admin/marketplace/categories/${id}`);
}

/**
 * PATCH with the full `CategoryUpdate` partial shape (`AdminCategoryPatch`).
 * Verified PATCH route: `admin_router.py:712-740` (`patch_category`, calls
 * `service.update_category(..., replace=False)`) — PUT on the same path is
 * full-replace and clears omitted optional fields, so every write from this
 * task uses this PATCH call, never PUT.
 */
export function patchCategory(
  id: string,
  body: AdminCategoryPatch,
): Promise<AdminCategory> {
  return apiFetch<AdminCategory, AdminCategoryPatch>({
    method: "PATCH",
    path: `/admin/marketplace/categories/${id}`,
    body,
  });
}

/**
 * `POST /admin/marketplace/categories/{id}/archive` — idempotent-in-effect
 * (`admin_router.py:775-804`). Distinct from `deleteCategory` (soft delete):
 * products keep their category, descendants keep their own `is_archived`
 * flag untouched.
 */
export function archiveCategory(id: string): Promise<AdminCategory> {
  return apiClient.post<AdminCategory, Record<string, never>>(
    `/admin/marketplace/categories/${id}/archive`,
    {},
  );
}

/** `POST /admin/marketplace/categories/{id}/unarchive` — reverses
 * `archiveCategory` (`admin_router.py:807-830`). */
export function unarchiveCategory(id: string): Promise<AdminCategory> {
  return apiClient.post<AdminCategory, Record<string, never>>(
    `/admin/marketplace/categories/${id}/unarchive`,
    {},
  );
}

// ---------------------------------------------------------------------
// Category aliases (PIM Wave P1 Task 6) — nested CRUD under a category.
// `admin_router.py:878-975`; DTOs `marketplace/schemas.py:690-762`.
// ---------------------------------------------------------------------

export function listCategoryAliases(
  categoryId: string,
): Promise<CategoryAliasListResponse> {
  return apiClient.get<CategoryAliasListResponse>(
    `/admin/marketplace/categories/${categoryId}/aliases`,
  );
}

/** 409 `marketplace.category_alias.duplicate` on a case-insensitive dup
 * per category (`service.py:124-135`) — the caller (`AliasesEditor`)
 * matches `ApiError.code` to render an inline error. */
export function createCategoryAlias(
  categoryId: string,
  body: CategoryAliasCreateBody,
): Promise<CategoryAlias> {
  return apiClient.post<CategoryAlias, CategoryAliasCreateBody>(
    `/admin/marketplace/categories/${categoryId}/aliases`,
    body,
  );
}

/** 204 hard delete (`admin_router.py:954-975`) — no confirm at the call
 * site, additive back (brief §2). */
export function deleteCategoryAlias(
  categoryId: string,
  aliasId: string,
): Promise<void> {
  return apiClient.delete<void>(
    `/admin/marketplace/categories/${categoryId}/aliases/${aliasId}`,
  );
}

// ---------------------------------------------------------------------
// Category merge (PIM Wave P1 Task 7) — `admin_router.py:833-867`.
// ---------------------------------------------------------------------

/** Body for `POST .../merge` — mirrors `CategoryMergeRequest`
 * (`marketplace/schemas.py:765-771`, `extra="forbid"`). */
export interface CategoryMergeRequestBody {
  target_category_id: string;
}

/**
 * Mirrors `CategoryMergeResponse` (`marketplace/schemas.py:774-789`) — the
 * carry-over counts from a category merge. All int except `source_archived`
 * (always `true` on success, per the schema docstring — kept explicit so
 * the client can render the terminal state without a second read). Only
 * available AFTER the merge commits (USER RULING C2 — no preview).
 */
export interface CategoryMergeResponse {
  products_repointed: number;
  links_moved: number;
  links_deduped: number;
  children_reparented: number;
  aliases_moved: number;
  aliases_dropped: number;
  source_archived: boolean;
}

/**
 * `POST /admin/marketplace/categories/{sourceId}/merge` — commits the merge
 * of `sourceId` INTO `body.target_category_id` in one transaction and
 * returns the carry-over counts. See the guard-code list in the file header.
 */
export function mergeCategory(
  sourceId: string,
  body: CategoryMergeRequestBody,
): Promise<CategoryMergeResponse> {
  return apiClient.post<CategoryMergeResponse, CategoryMergeRequestBody>(
    `/admin/marketplace/categories/${sourceId}/merge`,
    body,
  );
}
